/**
 * ReasoningStepsList Component
 *
 * Shared component that renders a list of reasoning steps.
 * Used by ReasoningSidebar (full mode) and InlineReasoningCard (compact mode,
 * inside ThinkingIndicator's collapsible panel).
 */

import { Building06 } from '@untitledui/icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';

import {
  getStageConfig,
  isStepInProgress,
  REASONING_DESIGN_TOKENS,
  type ToolResult,
} from './design-tokens';
import {
  ACTIVE_STEP_LABEL_CLASS,
  COMPACT_STEP_LABEL_CLASS,
  INLINE_TITLE_MARKDOWN_COMPONENTS,
} from './inlineStepTitle';
import { getAutoExpandedStepIds } from './reasoningExpansion';
import { ReasoningStep } from './ReasoningStep';
import {
  deriveStepTitle,
  resolveStepTitle,
  stepBodyAfterTitle,
} from './stepTitle';
import { ThinkingContent } from './ThinkingContent';
import { groupConsecutiveByToolName } from './toolGrouping';
import { ToolRowReveal } from './ToolRowReveal';
import { ToolUseGroup } from './ToolUseGroup';
import { ToolUseItem } from './ToolUseItem';

export interface ReasoningStepData {
  id: string;
  step_number: number;
  stage: string;
  content: string;
  status: string;
  is_final: boolean;
  tokens_used?: number;
  tool_results?: ToolResult[];
  artifacts?: Array<{
    id: number;
    title: string;
    artifact_type: { name: string };
    status: string;
    version_number: number;
    parent_artifact?: number;
    is_current_version?: boolean;
    associated_action_id?: number | null;
    associated_action?: unknown;
  }>;
  dynamic_views?: Array<{
    id: number;
    title: string;
    view_type: { name: string; description?: string };
    status: string;
    version_number: number;
    created_at: string;
  }>;
}

export interface ReasoningStepsListProps {
  steps: ReasoningStepData[];
  onOpenDynamicViewSidebar?: (viewId: number) => void;
  /** Unique identifier for this list (used for expanded state reset) */
  listId?: string;
  /** Whether to show in compact mode (for inline card) */
  compact?: boolean;
  /** Optional ref to scroll container for auto-scroll behavior */
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
  /** Organization ID threaded from parent for entity badge clickability. When undefined, badges render as non-interactive. */
  orgId?: number;
}

const DYNAMIC_VIEW_ICON = (
  <Building06 className='w-[18px] h-[18px] text-fg-primary' strokeWidth={1.5} />
);

interface DynamicViewItemProps {
  view: { id: number; title: string };
  isFirst: boolean;
  isLast: boolean;
  onOpenDynamicViewSidebar?: (viewId: number) => void;
}

const DynamicViewItem: React.FC<DynamicViewItemProps> = ({
  view,
  isFirst,
  isLast,
  onOpenDynamicViewSidebar,
}) => {
  const handleClick = useCallback(() => {
    onOpenDynamicViewSidebar?.(view.id);
  }, [onOpenDynamicViewSidebar, view.id]);

  return (
    <ReasoningStep.SubItem
      icon={DYNAMIC_VIEW_ICON}
      isFirst={isFirst}
      isLast={isLast}
    >
      <button
        onClick={handleClick}
        className='text-left hover:underline cursor-pointer'
      >
        <span>Ahoy created </span>
        <span className='underline'>{view.title}</span>
      </button>
    </ReasoningStep.SubItem>
  );
};

interface ReasoningStepAccordionItemProps {
  step: ReasoningStepData;
  isExpanded: boolean;
  isLast: boolean;
  setExpandedSteps: React.Dispatch<React.SetStateAction<Set<string>>>;
  onOpenDynamicViewSidebar?: (viewId: number) => void;
  compact?: boolean;
  orgId?: number;
}

const ReasoningStepAccordionItem: React.FC<ReasoningStepAccordionItemProps> = ({
  step,
  isExpanded,
  isLast,
  setExpandedSteps,
  onOpenDynamicViewSidebar,
  compact,
  orgId,
}) => {
  const config = getStageConfig(step.stage);

  const { statusText: activeStatusText } = config;
  let statusText = '';
  if (isStepInProgress(step.status)) {
    statusText = activeStatusText;
  } else if (step.status === 'waiting_human') {
    statusText = 'Waiting for input';
  }

  const isActiveCompact = compact && isStepInProgress(step.status);
  let compactLabelClass = COMPACT_STEP_LABEL_CLASS;
  if (isActiveCompact) {
    compactLabelClass = ACTIVE_STEP_LABEL_CLASS;
  }

  // Compact header/body split (avoids the title duplicating the body):
  //   title = the step's first sentence (a natural gist — no keyword guessing)
  //   body  = everything AFTER the first sentence (empty for 1-sentence steps)
  // A single-sentence step therefore shows just its title, no repeated prose.
  // Memoized so the regex-heavy derivation only reruns when an input it
  // actually reads changes, not on every render (e.g. a sibling step's
  // expand/collapse toggling this component's props).
  const { compactTitle, compactBody } = useMemo(() => {
    if (!compact) {
      return { compactTitle: '', compactBody: '' };
    }
    // Computed once and passed into stepBodyAfterTitle below — avoids
    // deriving the title a second time just to check its truncation state.
    // Steps with no narration text (weaker models emit bare tool calls) fall
    // back to a tool-count summary ("Thinking · 2 tools") before the bare
    // stage label; the tool rows under the header carry the names themselves.
    const derivedTitle = deriveStepTitle(step.content);
    return {
      // Single source of title precedence (narration → tool summary → live
      // status → stage name), shared with the "Thinking…" label so the two
      // never drift. The body still splits on the raw narration title.
      compactTitle: resolveStepTitle(step),
      compactBody: stepBodyAfterTitle(step.content, derivedTitle),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- statusText/config are derived from step.status/step.stage; listed explicitly instead of the derived values
  }, [step.content, step.tool_results, compact, step.status, step.stage]);
  // Safety net: if the single-sentence title hit the 120-char cap in
  // deriveStepTitle (it appends "…"), the body is empty and the truncated tail
  // would be unreachable. Fall back to step.content so users can still read the
  // full text on expand. Short single-sentence steps keep the design's empty
  // body and show only the title.
  //
  // The body renders whenever the step is expanded — including the
  // auto-expanded latest/streaming step — so the live step's narration is
  // visible without a click. Non-latest steps stay collapsed in compact mode
  // until the user opens them.
  const compactBodyContent =
    compactBody || (compactTitle.endsWith('…') ? step.content : '');

  const handleExpandedChange = useCallback(
    (isOpen: boolean) => {
      setExpandedSteps(prev => {
        const next = new Set(prev);
        if (isOpen) {
          next.add(step.id);
        } else {
          next.delete(step.id);
        }
        return next;
      });
    },
    [step.id, setExpandedSteps]
  );

  return (
    <ReasoningStep
      ariaLabel={`${config.displayName} step: ${step.status}`}
      isLast={isLast}
    >
      <ReasoningStep.Icon stage={step.stage} />
      <ReasoningStep.Content>
        {/* Migrated from ReasoningStep.Header / ReasoningStep.Body
            (hand-rolled accordion) to shared <AccordionItem>. The
            outer column layout (icon + content) stays compound;
            only the expand/collapse mechanics inside Content
            switch to the shared lib. */}
        <AccordionItem
          isExpanded={isExpanded}
          onExpandedChange={handleExpandedChange}
        >
          <AccordionTrigger
            className={`w-full ${REASONING_DESIGN_TOKENS.layout.headerRow} cursor-pointer rounded-lg transition-colors ${REASONING_DESIGN_TOKENS.states.hoverBg} -mx-2 px-2`}
          >
            <div className='flex items-center gap-2 min-w-0 h-6'>
              {compact ? (
                // Title = the step's first sentence (the gist). The remaining
                // sentences render in the body below, so this never repeats it.
                <MarkdownContent
                  content={compactTitle}
                  orgId={orgId}
                  size='compact'
                  as='span'
                  entityInteractive={false}
                  components={INLINE_TITLE_MARKDOWN_COMPONENTS}
                  className={`${compactLabelClass} truncate`}
                />
              ) : (
                <>
                  <span
                    className={`${REASONING_DESIGN_TOKENS.typography.sectionTitle} ${REASONING_DESIGN_TOKENS.colors.sectionTitle}`}
                  >
                    {config.displayName}
                  </span>
                  {statusText && (
                    <span
                      className={`${REASONING_DESIGN_TOKENS.typography.statusSubtitle} ${REASONING_DESIGN_TOKENS.colors.statusSubtitle} truncate relative top-[1px]`}
                    >
                      {statusText}
                    </span>
                  )}
                </>
              )}
            </div>
            <AccordionChevron
              className={
                compact ? 'w-4 h-4 text-fg-tertiary' : 'w-6 h-6 text-fg-primary'
              }
              strokeWidth={1.5}
            />
          </AccordionTrigger>

          <AccordionPanel className={compact ? 'mt-1' : 'mt-2'}>
            <div
              tabIndex={0}
              aria-label='Step content, scrollable'
              className={`${REASONING_DESIGN_TOKENS.typography.contentText} ${REASONING_DESIGN_TOKENS.colors.contentText} leading-[20px] overflow-x-auto`}
            >
              {/* Main content. In compact (inline chat) mode the thinking
                  block is clamped to a fixed window that gently auto-scrolls
                  (teleprompter) when it overflows, so long multi-paragraph
                  reasoning reads as alive without dominating the chat. The
                  full content stays available, unclamped, in the non-compact
                  sidebar history view. */}
              {/* Compact: render only the body remainder (sentences after the
                  title) so the header isn't repeated; empty for 1-sentence
                  steps. Non-compact (sidebar): render the full content. */}
              {compact
                ? compactBodyContent && (
                    <div className='mb-1.5 text-sm text-fg-secondary'>
                      <ThinkingContent
                        animate={isExpanded}
                        contentKey={compactBodyContent}
                      >
                        <MarkdownContent
                          content={compactBodyContent}
                          size='compact'
                          orgId={orgId}
                        />
                      </ThinkingContent>
                    </div>
                  )
                : step.content && (
                    <div className='mb-3'>
                      <MarkdownContent
                        content={step.content}
                        size='compact'
                        orgId={orgId}
                      />
                    </div>
                  )}

              {/* Tool results pulled in tight directly under the reasoning —
                  no "Tool Calls (N)" label; each row already shows its tool
                  name and call-count chip. */}
              {step.tool_results && step.tool_results.length > 0 && (
                <div className={step.content ? 'mt-1.5' : ''}>
                  <div className='space-y-0'>
                    {groupConsecutiveByToolName(step.tool_results).map(
                      (group, groupIndex, groups) => {
                        const isFirst = groupIndex === 0;
                        const isLast = groupIndex === groups.length - 1;
                        // Tool results have no unique ID; same tool may appear
                        // multiple times, so key by tool name + group position.
                        const key = `${group[0].tool_name}-${groupIndex}`;
                        const row =
                          group.length === 1 ? (
                            <ToolUseItem
                              toolResult={group[0]}
                              isFirst={isFirst}
                              isLast={isLast}
                            />
                          ) : (
                            <ToolUseGroup
                              toolResults={group}
                              isFirst={isFirst}
                              isLast={isLast}
                            />
                          );
                        // Cascade the tools in (compact chat only); the sidebar
                        // history view renders them plain.
                        return (
                          <ToolRowReveal
                            key={key}
                            index={groupIndex}
                            animate={!!compact}
                          >
                            {row}
                          </ToolRowReveal>
                        );
                      }
                    )}
                  </div>
                </div>
              )}

              {/* Dynamic Views as sub-items */}
              {step.dynamic_views && step.dynamic_views.length > 0 && (
                <div className='mt-3 space-y-0'>
                  {step.dynamic_views.map((view, viewIndex, arr) => (
                    <DynamicViewItem
                      key={view.id}
                      view={view}
                      isFirst={viewIndex === 0}
                      isLast={viewIndex === arr.length - 1}
                      onOpenDynamicViewSidebar={onOpenDynamicViewSidebar}
                    />
                  ))}
                </div>
              )}
            </div>
          </AccordionPanel>
        </AccordionItem>
      </ReasoningStep.Content>
    </ReasoningStep>
  );
};

// Memoized so a sibling step's expand/collapse toggle (which changes
// setExpandedSteps' referent but not this step's own props) doesn't force
// every other step to redo its title/body derivation.
const MemoizedAccordionItem = React.memo(ReasoningStepAccordionItem);

export const ReasoningStepsList: React.FC<ReasoningStepsListProps> = ({
  steps,
  onOpenDynamicViewSidebar,
  listId,
  compact = false,
  scrollContainerRef,
  orgId,
}) => {
  // Filter to non-final steps
  const reasoningSteps = steps.filter(step => !step.is_final);

  // Track which steps are expanded
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(() =>
    getAutoExpandedStepIds(reasoningSteps, compact)
  );

  // Reset expanded state when list changes (e.g., switching chains)
  // Intentionally only depends on listId - we reset when switching chains,
  // not when steps within a chain change (that's handled by the next effect)
  useEffect(() => {
    setExpandedSteps(getAutoExpandedStepIds(reasoningSteps, compact));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId, compact]);

  // Auto-expand new steps as they arrive.
  // - compact: keep only the latest step open (completed steps collapse).
  // - full: additively expand new steps, preserving the user's collapsed state
  //   for steps they've already closed.
  useEffect(() => {
    setExpandedSteps(prev => {
      if (compact) {
        return getAutoExpandedStepIds(reasoningSteps, true);
      }
      const newSet = new Set(prev);
      for (const step of reasoningSteps) {
        // Only add steps we haven't seen before (don't re-expand collapsed ones)
        if (!prev.has(step.id) && prev.size > 0) {
          newSet.add(step.id);
        } else if (prev.size === 0) {
          // Initial load - expand all
          newSet.add(step.id);
        }
      }
      return newSet;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only rerun when step count changes; reasoningSteps is a new array ref each render
  }, [reasoningSteps.length, compact]);

  // Auto-scroll to bottom when new steps arrive (staggered scroll effect)
  useEffect(() => {
    if (!scrollContainerRef?.current) {
      return;
    }

    // Small delay to ensure DOM has updated with new content
    const timeoutId = setTimeout(() => {
      if (!scrollContainerRef.current) {
        return;
      }

      const container = scrollContainerRef.current;
      const targetScroll = container.scrollHeight - container.clientHeight;
      const startScroll = container.scrollTop;
      const distance = targetScroll - startScroll;

      if (distance <= 0) {
        return;
      }

      const duration = 300; // snappy follow-scroll as steps stream in
      const startTime = performance.now();

      const animateScroll = (currentTime: number) => {
        if (!scrollContainerRef.current) {
          return;
        }

        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease-out cubic for a natural deceleration
        const easeOut = 1 - Math.pow(1 - progress, 3);

        scrollContainerRef.current.scrollTop = startScroll + distance * easeOut;

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };

      requestAnimationFrame(animateScroll);
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [reasoningSteps.length, scrollContainerRef]);

  if (reasoningSteps.length === 0) {
    return (
      <div className='text-center py-4 text-tertiary text-sm'>
        No reasoning steps available
      </div>
    );
  }

  return (
    <div className='space-y-0'>
      {reasoningSteps.map((step, stepIndex) => {
        const item = (
          <MemoizedAccordionItem
            step={step}
            isExpanded={expandedSteps.has(step.id)}
            isLast={stepIndex === reasoningSteps.length - 1}
            setExpandedSteps={setExpandedSteps}
            onOpenDynamicViewSidebar={onOpenDynamicViewSidebar}
            compact={compact}
            orgId={orgId}
          />
        );
        // In the inline chat, each step elegantly resolves into focus as it
        // arrives (blur-in + drift + fade). Keyed by step.id so it runs once
        // on mount, not on every poll re-render. Sidebar history renders plain.
        return compact ? (
          <div key={step.id} className='animate-thinking-in'>
            {item}
          </div>
        ) : (
          <React.Fragment key={step.id}>{item}</React.Fragment>
        );
      })}
    </div>
  );
};

export default ReasoningStepsList;
